import { UseCase } from "./types";

export const useCases: UseCase[] = [
  {
    slug: "adhd",
    title: "ADHD & Executive Function",
    shortTitle: "Find your starting point",
    category: "Daily functioning",
    icon: "focus",
    headline: "Knowing what to do is only half the story.",
    description:
      "Practical support for getting started, finding focus, and following through. At a pace that works for your brain.",
    scene: "At your desk. A blank document. A busy mind.",
    time: "14:10",
    tone: "lavender",
    problem:
      "The task is clear. Starting it is not. When planning, time, or transitions take more effort, another to-do list can feel like one more thing to manage.",
    moments: [
      {
        time: "09:00",
        title: "Everything feels urgent",
        detail:
          "Find one useful priority instead of trying to solve the whole day.",
      },
      {
        time: "14:10",
        title: "The first step feels too big",
        detail:
          "Turn “write the report” into opening the document and adding one sentence.",
      },
      {
        time: "17:30",
        title: "Time slipped away",
        detail:
          "Pause, reset expectations, and make a workable plan for what is left.",
      },
    ],
    help: [
      [
        "Make the start smaller",
        "Break a large intention into an action you can actually begin.",
      ],
      [
        "Bring intentions back",
        "An agreed check-in can return your attention to a plan without relying on you to open an app.",
      ],
      [
        "Adjust without judgment",
        "Rework the plan when energy, time, or priorities change.",
      ],
    ],
    conversation: [
      { from: "person", text: "I know what I need to do. I just can’t start." },
      {
        from: "systein",
        text: "Let’s make the starting point ridiculously small. What could you do in two minutes?",
      },
    ],
    context: [
      ["Goal", "Start the report"],
      ["Preference", "One step at a time"],
      ["Check-in", "This afternoon"],
    ],
    individual:
      "Explore Irene, a Systein experience for planning, reminders, and everyday follow-through.",
    organization:
      "Explore support for the everyday barriers that sit between an agreed goal and taking action.",
    organizationRoute: "/solutions/education",
    boundary:
      "Support for everyday functioning does not diagnose ADHD or replace assessment, therapy, or medication advice.",
    companion: "Irene",
    related: ["student-support", "neurodivergent-support", "stress-anxiety"],
  },
  {
    slug: "stress-anxiety",
    title: "Stress & Anxiety",
    shortTitle: "A little room to breathe",
    category: "Mental wellbeing",
    icon: "heart",
    headline: "Meet the moment with a little more steadiness.",
    description:
      "Space to prepare, put thoughts into words, and find a manageable next step when everyday life feels like a lot.",
    scene: "On the way to a meeting that is on your mind.",
    time: "08:45",
    tone: "peach",
    problem:
      "A difficult conversation or an uncertain day can occupy much more space than the event itself. Useful support starts with what is happening now.",
    moments: [
      {
        time: "08:45",
        title: "Before a difficult meeting",
        detail:
          "Prepare the point you want to make and the question you want to ask.",
      },
      {
        time: "16:00",
        title: "After a demanding day",
        detail: "Notice what took energy and choose a small way to recover.",
      },
      {
        time: "21:15",
        title: "When thoughts keep circling",
        detail:
          "Separate what can wait from the next thing within your control.",
      },
    ],
    help: [
      [
        "Prepare together",
        "Rehearse a conversation or put an upcoming situation into smaller steps.",
      ],
      [
        "Notice patterns",
        "Reflect on the circumstances that tend to make a day harder.",
      ],
      [
        "Make space for recovery",
        "Build short pauses and familiar grounding routines into daily life.",
      ],
    ],
    conversation: [
      {
        from: "systein",
        text: "You mentioned this meeting was making you nervous. Want to prepare for it together?",
      },
      { from: "person", text: "I’m worried I’ll forget what I wanted to say." },
      {
        from: "systein",
        text: "Let’s choose the one thing you most want them to understand.",
      },
    ],
    context: [
      ["Upcoming", "Team meeting"],
      ["Recent", "Feeling uncertain"],
      ["Preference", "Gentle encouragement"],
    ],
    individual:
      "Tell us which moments you would like support with. We can discuss whether an existing experience is a good fit.",
    organization:
      "Explore an everyday wellbeing experience with clear boundaries and routes to human support.",
    organizationRoute: "/solutions/employers",
    boundary:
      "These examples describe everyday wellbeing support. Systein does not diagnose or treat anxiety disorders and is not a crisis service.",
    related: ["sleep", "between-session-support", "adhd"],
  },
  {
    slug: "sleep",
    title: "Sleep & Recovery",
    shortTitle: "Let the day come to a close",
    category: "Healthy behaviors",
    icon: "moon",
    headline: "A gentler end to the day. A more realistic start.",
    description:
      "Explore support for winding down, noticing patterns, and adjusting expectations after a difficult night.",
    scene: "The house is quiet. Your mind is still busy.",
    time: "23:30",
    tone: "night",
    problem:
      "Bedtime is not always the end of the day in your head. And a poor night can make tomorrow’s carefully made plan feel impossible.",
    moments: [
      {
        time: "22:00",
        title: "The evening keeps stretching",
        detail: "Reconnect with the wind-down routine you wanted to try.",
      },
      {
        time: "23:30",
        title: "Your mind will not switch off",
        detail: "Put tomorrow’s loose ends somewhere you can return to them.",
      },
      {
        time: "07:30",
        title: "A lighter plan is needed",
        detail: "Use your own reflection on sleep to set realistic priorities.",
      },
    ],
    help: [
      [
        "Create a familiar rhythm",
        "Work toward a repeatable evening routine with room for real life.",
      ],
      [
        "Reflect without scoring yourself",
        "Look for connections between your day, habits, and how rested you feel.",
      ],
      [
        "Adapt the next day",
        "Choose what matters most when your energy is lower than expected.",
      ],
    ],
    conversation: [
      {
        from: "person",
        text: "I’m tired, but I keep thinking about tomorrow.",
      },
      {
        from: "systein",
        text: "Want to get the loose ends out of your head? We can leave them in a short list for the morning.",
      },
    ],
    context: [
      ["Time", "23:30"],
      ["Routine", "Wind down"],
      ["Preference", "Short, quiet check-ins"],
    ],
    individual:
      "Explore how routine support could fit your evenings and mornings. Ask us about available experiences.",
    organization:
      "Discuss recovery and routine support within an employee or student wellbeing program.",
    organizationRoute: "/solutions/employers",
    boundary:
      "Systein does not diagnose sleep conditions or offer medical sleep treatment. Connected sleep data is an integration concept, not a currently verified connection.",
    related: ["stress-anxiety", "weight-management", "student-support"],
  },
  {
    slug: "weight-management",
    title: "Weight Management",
    shortTitle: "Make the next choice easier",
    category: "Healthy behaviors",
    icon: "leaf",
    headline: "Small choices. Real life. Support that stays.",
    description:
      "Build sustainable routines around food, movement, and reflection, with support for the moments when plans meet everyday life.",
    scene: "In the kitchen, after a long workday.",
    time: "19:42",
    tone: "sage",
    problem:
      "A plan made on Sunday has to survive a tiring Wednesday. Food decisions happen around work, stress, family, energy, and everything else.",
    moments: [
      {
        time: "12:15",
        title: "The day changed your plans",
        detail:
          "Find a reasonable option that fits the time and food available.",
      },
      {
        time: "19:42",
        title: "Cooking feels like too much",
        detail:
          "Make dinner simpler without turning it into a test of willpower.",
      },
      {
        time: "20:37",
        title: "The evening gets difficult",
        detail: "Reflect on cravings and what would feel supportive right now.",
      },
    ],
    help: [
      [
        "Start with your real day",
        "Discuss decisions in the context of energy, preferences, and practical limits.",
      ],
      [
        "Build repeatable habits",
        "Choose small routines around meals and movement that you can return to.",
      ],
      [
        "Keep reflection kind",
        "Notice progress and obstacles without shame, rigid targets, or a perfect-day mentality.",
      ],
    ],
    conversation: [
      { from: "systein", text: "Long day?" },
      { from: "person", text: "Yeah. I really don’t feel like cooking." },
      {
        from: "systein",
        text: "You don’t need a perfect dinner. Want to figure out the easiest reasonable option together?",
      },
    ],
    context: [
      ["Goal", "Sustainable habits"],
      ["Recent", "Busy workday"],
      ["Preference", "Simple suggestions"],
    ],
    individual:
      "Meet Sara — Weight Management Companion. An existing Systein experience available through WhatsApp.",
    organization:
      "Discuss everyday follow-through alongside the goals people agree with their dietitian or care team.",
    organizationRoute: "/solutions/healthcare",
    boundary:
      "Sara supports routines and reflection. It does not replace a dietitian, prescribe diets, or provide medical advice. There are no guaranteed weight outcomes.",
    companion: "Sara",
    related: [
      "sleep",
      "between-session-support",
      "chronic-condition-management",
    ],
  },
  {
    slug: "neurodivergent-support",
    title: "Autism & Neurodivergent Support",
    shortTitle: "More predictability. Your way.",
    category: "Daily functioning",
    icon: "spark",
    headline: "Support that makes room for how you experience life.",
    description:
      "Explore practical support for transitions, routines, communication, and uncertainty, shaped around individual preferences.",
    scene: "A familiar routine. An unexpected change.",
    time: "08:20",
    tone: "lavender",
    problem:
      "A change of plan can involve far more than a change of time. Sensory needs, uncertainty, and preparing what to say all deserve space.",
    moments: [
      {
        time: "08:20",
        title: "A route has changed",
        detail: "Walk through what is different and what can stay familiar.",
      },
      {
        time: "12:00",
        title: "An unfamiliar event is ahead",
        detail:
          "Prepare the sequence, possible sensory demands, and available choices.",
      },
      {
        time: "18:00",
        title: "Time to change activities",
        detail: "Use a transition reminder in the style and timing you prefer.",
      },
    ],
    help: [
      [
        "Prepare for what is ahead",
        "Break an event into a clear sequence and identify useful questions.",
      ],
      [
        "Respect preferences",
        "Make room for communication style, sensory considerations, and the need for predictability.",
      ],
      [
        "Support autonomy",
        "Offer options and practical structure while the person stays in control.",
      ],
    ],
    conversation: [
      {
        from: "person",
        text: "The room for tomorrow has changed. I don’t know what to expect.",
      },
      {
        from: "systein",
        text: "Let’s work through what you know, what you want to ask, and what would make arriving easier.",
      },
    ],
    context: [
      ["Upcoming", "New meeting room"],
      ["Preference", "Clear sequences"],
      ["Helpful", "Advance preparation"],
    ],
    individual:
      "Discuss the routines and transitions you would like help with, and whether Systein is suitable for you.",
    organization:
      "Explore optional, individual-led support alongside existing accessibility and inclusion services.",
    organizationRoute: "/solutions/education",
    boundary:
      "Neurodivergence is not something to fix. Examples illustrate possible support, not a dedicated clinical service or a substitute for accommodations.",
    related: ["adhd", "student-support", "stress-anxiety"],
  },
  {
    slug: "student-support",
    title: "Student Support",
    shortTitle: "One deadline at a time",
    category: "Daily functioning",
    icon: "book",
    headline: "A little structure for a life with a lot going on.",
    description:
      "Study, deadlines, new routines, and everything in between. Explore support that helps students find their next manageable step.",
    scene: "In the library. Three deadlines, one afternoon.",
    time: "15:20",
    tone: "peach",
    problem:
      "University brings freedom and a lot of invisible organizing. Knowing how to ask for support can be another task on an already full list.",
    moments: [
      {
        time: "09:15",
        title: "A week full of deadlines",
        detail: "See the order of tasks and make a plan with room to breathe.",
      },
      {
        time: "15:20",
        title: "Study time is slipping away",
        detail: "Turn the next block of time into one specific action.",
      },
      {
        time: "22:30",
        title: "An exam is on your mind",
        detail: "Prepare for tomorrow and leave space for sleep.",
      },
    ],
    help: [
      [
        "Make priorities visible",
        "Sort competing tasks by what needs attention first.",
      ],
      [
        "Keep plans flexible",
        "Adjust study blocks around energy, classes, and actual progress.",
      ],
      [
        "Stay connected to support",
        "Prepare questions for a tutor or student adviser when a human conversation would help.",
      ],
    ],
    conversation: [
      {
        from: "person",
        text: "I have three things due and keep switching between them.",
      },
      {
        from: "systein",
        text: "Which deadline comes first? Let’s give just that one the next ten minutes.",
      },
    ],
    context: [
      ["Upcoming", "Friday deadline"],
      ["Goal", "Start the outline"],
      ["Preference", "Short study blocks"],
    ],
    individual:
      "Explore Irene for planning and follow-through, or tell us about the student support you need.",
    organization:
      "Help students stay on track when nobody is standing next to them. Discuss a defined pilot with your student support team.",
    organizationRoute: "/solutions/education",
    boundary:
      "Support complements tutors, accessibility services, and student wellbeing teams. Examples do not imply an existing university deployment.",
    companion: "Irene",
    related: ["adhd", "sleep", "stress-anxiety"],
  },
  {
    slug: "between-session-support",
    title: "Between-Session Support",
    shortTitle: "Keep the conversation going",
    category: "Care continuity",
    icon: "calendar",
    headline: "Support doesn’t have to stop when the appointment ends.",
    description:
      "Explore a bridge between professional guidance and the everyday moments where people put it into practice.",
    scene: "At home. Putting an agreed goal into practice.",
    time: "18:15",
    tone: "sage",
    problem:
      "An appointment can establish a useful goal. The following days reveal the practical barriers, questions, and changes that determine what happens next.",
    moments: [
      {
        time: "After a visit",
        title: "Turn a goal into a first step",
        detail: "Reflect on the agreed action and what it means for today.",
      },
      {
        time: "During the week",
        title: "Notice what gets in the way",
        detail: "Capture a short reflection while the experience is fresh.",
      },
      {
        time: "Before a visit",
        title: "Prepare the conversation",
        detail: "Collect the questions and experiences you want to bring back.",
      },
    ],
    help: [
      [
        "Support agreed intentions",
        "Keep the person’s own goals and professionally agreed plan in view.",
      ],
      [
        "Make reflection easier",
        "Use short check-ins to put everyday successes and obstacles into words.",
      ],
      [
        "Keep professionals connected",
        "Define what should be shared, with whom, and which situations require human follow-up before a pilot.",
      ],
    ],
    conversation: [
      {
        from: "systein",
        text: "Yesterday you and your dietitian agreed on one small goal for this week. How has it been going?",
      },
      {
        from: "person",
        text: "The evening walk worked, but not on my late shift.",
      },
      {
        from: "systein",
        text: "Would you like to note that for your next appointment and think about an easier option for late shifts?",
      },
    ],
    context: [
      ["Goal", "Evening walk"],
      ["Upcoming", "Thursday appointment"],
      ["Recent", "Late shift"],
    ],
    individual:
      "Ask about using support alongside your existing professional relationship and agreed goals.",
    organization:
      "Define a between-session pilot with clear responsibilities, consent, evaluation, and human follow-up.",
    organizationRoute: "/solutions/healthcare",
    boundary:
      "This is a proposed care-support workflow. Systein does not make clinical decisions, change care plans, or provide continuous professional monitoring.",
    related: [
      "chronic-condition-management",
      "weight-management",
      "stress-anxiety",
    ],
  },
  {
    slug: "chronic-condition-management",
    title: "Chronic Condition Self-Management",
    shortTitle: "Support for the longer journey",
    category: "Care continuity",
    icon: "activity",
    headline: "Everyday life continues. Support can, too.",
    description:
      "Explore practical support for routines, reflection, and the long-term work of following an agreed care plan.",
    scene: "A normal morning. Making room for your routine.",
    time: "08:05",
    tone: "sage",
    problem:
      "Long-term self-management is woven into ordinary days. Motivation, habits, and remembering what to discuss can all change between appointments.",
    moments: [
      {
        time: "Morning",
        title: "Return to a routine",
        detail:
          "Reconnect with the daily actions already agreed with your care team.",
      },
      {
        time: "Afternoon",
        title: "Notice a change",
        detail:
          "Put an experience into your own words for a professional conversation.",
      },
      {
        time: "Before a visit",
        title: "Remember what matters",
        detail: "Gather questions and reflections for the next appointment.",
      },
    ],
    help: [
      [
        "Stay with the agreed plan",
        "Use practical reminders and reflection around existing professional guidance.",
      ],
      [
        "Keep habits realistic",
        "Adjust how you organize daily activities when life becomes more demanding.",
      ],
      [
        "Prepare for human care",
        "Keep track of the questions you want a professional to help answer.",
      ],
    ],
    conversation: [
      {
        from: "systein",
        text: "You have your appointment tomorrow. Want to collect what you wanted to bring up?",
      },
      {
        from: "person",
        text: "Yes. My afternoon routine has been hard to keep.",
      },
      {
        from: "systein",
        text: "Let’s start there. What has been getting in the way?",
      },
    ],
    context: [
      ["Upcoming", "Appointment tomorrow"],
      ["Goal", "Keep a daily routine"],
      ["Preference", "One question at a time"],
    ],
    individual:
      "Discuss suitability with your professional and ask us about support for your everyday routines.",
    organization:
      "Explore a carefully scoped self-management experience within an existing care program.",
    organizationRoute: "/solutions/healthcare",
    boundary:
      "This is a potential support area, not a condition-specific medical product. Systein does not interpret symptoms, recommend medication changes, or provide emergency monitoring.",
    related: ["between-session-support", "sleep", "weight-management"],
  },
];

export const getUseCase = (slug: string) =>
  useCases.find((item) => item.slug === slug);
